import { yupResolver } from '@hookform/resolvers/yup';
import {
  useCreatePasswordlessUser,
  useCreateProfile,
  useUpdateProfile,
  useUpdateProfileLinks,
  useUpdateProfilePhoto,
} from '@sunrise-ui/api-client';
import {
  AuthFlowError,
  Button,
  ButtonVariants,
  Colors,
  InputError,
  Modal,
  TextInput,
  TextReg,
  getFileFromImage,
  useDynamicStyles,
  useSnackbar,
} from '@sunrise-ui/primitives';
import { isAxiosError } from 'axios';
import * as ImagePicker from 'expo-image-picker';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Image, StyleSheet, View } from 'react-native';
import { object, string } from 'yup';

export type CreateUserModalProps = {
  visible: boolean;
  hide: () => void;
  onCreated?: (userId: string) => void | Promise<void>;
  // Optional extra fields for richer flows (e.g., speakers)
  showLinkedin?: boolean;
  showBio?: boolean;
  showHeadline?: boolean;
  allowHeadshotUpload?: boolean;
};

export function CreateUserModal({
  visible,
  hide,
  onCreated,
  showLinkedin = false,
  showBio = false,
  showHeadline = false,
  allowHeadshotUpload = false,
}: CreateUserModalProps) {
  const styles = useDynamicStyles(createStyles);
  const { showSnackbar } = useSnackbar();

  type FormValues = {
    email: string;
    firstName?: string;
    lastName?: string;
    headline?: string;
    linkedin?: string;
    bio?: string;
  };

  const schema = object().shape({
    email: string()
      .trim()
      .email('Please enter a valid email address')
      .required('Please enter an email address'),
    firstName: string().trim().optional(),
    lastName: string().trim().optional(),
    headline: string().trim().optional(),
    linkedin: string().trim().optional(),
    bio: string().trim().optional(),
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      headline: '',
      linkedin: '',
      bio: '',
    },
  });

  const { mutateAsync: createPasswordlessUser, isPending: isCreatingUser } =
    useCreatePasswordlessUser();
  const { mutateAsync: createProfile, isPending: isCreatingProfile } =
    useCreateProfile();
  const { mutateAsync: updateProfile, isPending: isUpdatingProfile } =
    useUpdateProfile();
  const { mutateAsync: updateProfileLinks, isPending: isUpdatingLinks } =
    useUpdateProfileLinks();
  const { mutateAsync: uploadProfilePhoto, isPending: isUploadingHeadshot } =
    useUpdateProfilePhoto();

  const isSubmitting =
    isCreatingUser ||
    isCreatingProfile ||
    isUpdatingProfile ||
    isUpdatingLinks ||
    isUploadingHeadshot;

  const [headshot, setHeadshot] = React.useState<
    ImagePicker.ImagePickerAsset | undefined
  >(undefined);

  const handlePickHeadshot = async () => {
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      aspect: [1, 1],
      quality: 1,
    });
    if (!result.canceled) {
      const [asset] = result.assets;
      if (asset) setHeadshot(asset);
    }
  };

  const onSubmit = async ({
    email,
    firstName,
    lastName,
    headline,
    linkedin,
    bio,
  }: FormValues) => {
    try {
      const user = await createPasswordlessUser({ email });
      const userId = user.id;
      const profileInput = {
        user_id: userId,
        first_name: firstName || undefined,
        last_name: lastName || undefined,
        headline: showHeadline ? headline || undefined : undefined,
        bio: showBio ? bio || undefined : undefined,
      };
      try {
        await updateProfile(profileInput);
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 404) {
          await createProfile(profileInput);
        } else {
          throw err;
        }
      }

      if (showLinkedin && linkedin) {
        await updateProfileLinks({ userId, links: { linkedin } });
      }

      if (allowHeadshotUpload && headshot) {
        try {
          const file = (await getFileFromImage(headshot, userId)) as File;
          await uploadProfilePhoto({ userId, file });
        } catch (error) {
          if (error instanceof Error && error.message.includes('AVIF')) {
            showSnackbar({ text: error.message, type: 'error' });
            return;
          }
          throw error;
        }
      }

      if (onCreated) await onCreated(userId);

      reset({
        email: '',
        firstName: '',
        lastName: '',
        headline: '',
        linkedin: '',
        bio: '',
      });
      setHeadshot(undefined);
      hide();
    } catch (err) {
      if (err instanceof AuthFlowError) return;
      console.error('CreateUserModal submit failed', err);
    }
  };

  const handleClose = () => {
    hide();
    reset({
      email: '',
      firstName: '',
      lastName: '',
      headline: '',
      linkedin: '',
      bio: '',
    });
    setHeadshot(undefined);
  };

  return (
    <Modal
      visible={visible}
      hide={handleClose}
      size="medium"
      title="Create User"
    >
      <View style={styles.container}>
        <View>
          <Controller
            control={control}
            name="email"
            render={({ field: { onChange, value } }) => (
              <TextInput
                label="Email *"
                value={value}
                onChange={onChange}
                autoCapitalize="none"
                keyboardType="email-address"
                error={!!errors.email}
              />
            )}
          />
          <InputError fieldError={errors.email} />
        </View>

        <View style={styles.row}>
          <View style={styles.col}>
            <Controller
              control={control}
              name="firstName"
              render={({ field: { onChange, value } }) => (
                <TextInput
                  label="First Name"
                  value={value}
                  onChange={onChange}
                  error={!!errors.firstName}
                />
              )}
            />
            <InputError fieldError={errors.firstName} />
          </View>
          <View style={styles.col}>
            <Controller
              control={control}
              name="lastName"
              render={({ field: { onChange, value } }) => (
                <TextInput
                  label="Last Name"
                  value={value}
                  onChange={onChange}
                  error={!!errors.lastName}
                />
              )}
            />
            <InputError fieldError={errors.lastName} />
          </View>
        </View>

        {showHeadline ? (
          <View>
            <Controller
              control={control}
              name="headline"
              render={({ field: { onChange, value } }) => (
                <TextInput
                  label="Headline"
                  value={value}
                  onChange={onChange}
                  error={!!errors.headline}
                />
              )}
            />
            <InputError fieldError={errors.headline} />
          </View>
        ) : null}

        {showLinkedin ? (
          <View>
            <Controller
              control={control}
              name="linkedin"
              render={({ field: { onChange, value } }) => (
                <TextInput
                  label="LinkedIn URL"
                  value={value}
                  onChange={onChange}
                  error={!!errors.linkedin}
                />
              )}
            />
            <InputError fieldError={errors.linkedin} />
          </View>
        ) : null}

        {showBio ? (
          <View>
            <Controller
              control={control}
              name="bio"
              render={({ field: { onChange, value } }) => (
                <TextInput
                  label="Bio"
                  value={value}
                  onChange={onChange}
                  multiline
                  error={!!errors.bio}
                />
              )}
            />
            <InputError fieldError={errors.bio} />
          </View>
        ) : null}

        {allowHeadshotUpload ? (
          <View style={styles.headshotContainer}>
            <TextReg>Headshot</TextReg>
            <View style={styles.headshotRow}>
              <View style={styles.headshotPreview}>
                {headshot ? (
                  <Image
                    source={{ uri: headshot.uri }}
                    style={styles.headshotImage}
                    resizeMode="cover"
                  />
                ) : (
                  <View style={styles.headshotPlaceholder}>
                    <TextReg style={styles.headshotPlaceholderText}>
                      No image
                    </TextReg>
                  </View>
                )}
              </View>
              <Button
                variant={ButtonVariants.WHITE}
                size="small"
                onPress={() => void handlePickHeadshot()}
              >
                {headshot ? 'Replace' : 'Upload'}
              </Button>
            </View>
          </View>
        ) : null}

        <View style={styles.buttonRow}>
          <Button
            variant={ButtonVariants.WHITE}
            onPress={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant={ButtonVariants.PURPLE}
            onPress={() => void handleSubmit(onSubmit)()}
            disabled={!isValid || isSubmitting}
            loading={isSubmitting}
          >
            Create
          </Button>
        </View>
      </View>
    </Modal>
  );
}

const createStyles = () =>
  StyleSheet.create({
    container: {
      gap: 16,
      width: '100%',
    },
    row: { flexDirection: 'row', gap: 16 },
    col: { flex: 1 },
    headshotContainer: {
      gap: 4,
    },
    headshotRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 16,
    },
    headshotPreview: {
      width: 80,
      height: 80,
      borderRadius: 8,
      backgroundColor: Colors.opacity10,
      overflow: 'hidden',
    },
    headshotImage: {
      width: '100%',
      height: '100%',
    },
    headshotPlaceholder: {
      width: '100%',
      height: '100%',
      justifyContent: 'center',
      alignItems: 'center',
    },
    headshotPlaceholderText: {
      fontSize: 12,
      color: Colors.opacity50,
    },
    buttonRow: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      gap: 12,
      marginTop: 8,
    },
  });
